import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, AUTH_ACTION_TYPE, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE, LogoutCooldownLimitAction } from "./logout-cooldown-limit.action";
import {
	LOGOUT_COOLDOWN_LIMIT_MESSAGE_CODE,
	LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT,
} from "./logout-cooldown-limit.const";
import { LogoutCooldownLimitService } from "./logout-cooldown-limit.service";

@Injectable()
export class LogoutCooldownLimitEffect {
	logoutCooldownLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.get),
			switchMap(() =>
				this.service.get().pipe(
					map((response) => this.action.getSuccess(response)),
					catchError((error: AppError) => of(this.action.getFail(error)))
				)
			)
		)
	);

	logoutCooldownLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.update),
			map((x: ActionWithPayload<any>) => x.payload),
			switchMap((params) =>
				this.service.update(params).pipe(
					map(() => this.action.updateSuccess()),
					catchError((error: AppError) => of(this.action.updateFail(error)))
				)
			)
		)
	);

	logoutCooldownLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.updateSuccess),
			map(() => this.action.get())
		)
	);

	logoutCooldownLimitDelete$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.delete),
			map((x: ActionWithPayload<any>) => x.payload),
			switchMap((params) =>
				this.service.delete(params).pipe(
					map(() => this.action.deleteSuccess()),
					catchError((error: AppError) => of(this.action.deleteFail(error)))
				)
			)
		)
	);

	logoutCooldownLimitDeleteSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.deleteSuccess),
			map(() => this.action.get())
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.reset())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapClearByContext$({
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.get]: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.get,
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.update]: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.update,
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.delete]: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.delete,
			}),
			this.messageService.map$({
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.getSuccess]: {
					context: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.get,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: "",
				},
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.getFail]: {
					context: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.get,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CODE.getError,
				},
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.updateSuccess]: {
					context: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CODE.updateSuccess,
				},
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.updateFail]: {
					context: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CODE.updateError,
				},
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.deleteSuccess]: {
					context: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.delete,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CODE.deleteSuccess,
				},
				[LOGOUT_COOLDOWN_LIMIT_ACTION_TYPE.deleteFail]: {
					context: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.delete,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: LOGOUT_COOLDOWN_LIMIT_MESSAGE_CODE.deleteError,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: LogoutCooldownLimitAction,
		private service: LogoutCooldownLimitService,
		private messageService: MessageService
	) {}
}
